import React, { useState, useRef } from "react";
import { Modal } from "antd";
import { withTranslation } from "react-i18next";
import useReport from "../../hooks/useReport";
import { exportMeasurementsFile } from "../../utils/exportMeasurementsFile";
import SelectionStep from "../ReportModalSteps/SelectionStep";
import TelemetryOptionsStep from "../ReportModalSteps/TelemetryOptionsStep";
import DownloadingStep from "../ReportModalSteps/DownloadingStep";

function ReportModal({
  visible,
  onClose,
  onExportReport,
  telemetryInfo,
  alertsList,
  isStandalone,
  variant,
}) {
  const { fetchReportData } = useReport();
  const [selectedOption, setSelectedOption] = useState("simpleReport");
  const [step, setStep] = useState("selection");
  const [showLoading, setShowLoading] = useState(false);
  const [allowCancel, setAllowCancel] = useState(true);
  const [telemetrySelection, setTelemetrySelection] = useState(
    isStandalone ? ["temp"] : ["temp", "humidity"],
  );

  const isCancelled = useRef(false);
  const shipmentCode = new URLSearchParams(window.location.search).get(
    "shipmentCode",
  );

  const resetModal = () => {
    setSelectedOption("simpleReport");
    setStep("selection");
    setShowLoading(false);
    setAllowCancel(true);
    setTelemetrySelection(isStandalone ? ["temp"] : ["temp", "humidity"]);
  };

  const handleCancel = () => {
    isCancelled.current = true;
    onClose();
    resetModal();
  };

  const handleReportDownload = async (reportType, fullTelemetry) => {
    setStep("downloading");
    setShowLoading(true);
    isCancelled.current = false;

    const reportResponse = await fetchReportData(
      shipmentCode,
      reportType,
      fullTelemetry,
    );
    if (isCancelled.current) return;

    if (!reportResponse || reportResponse.statusCode !== 200) {
      setShowLoading(false);
      onClose();
      return Modal.error({
        title: "Error al exportar Informe o Mediciones",
        content:
          "Error al exportar el documento seleccionado.\nPor favor, inténtelo de nuevo más tarde.",
      });
    }

    await onExportReport({
      reportType,
      telemetryInfo,
      alertsList,
      reportData: reportResponse?.data,
      isCancelled: () => isCancelled.current,
      setAllowCancel,
      isStandalone,
      variant,
    });

    onClose();
    resetModal();
  };

  const exportTelemetryFile = () => {
    setStep("downloading");
    setShowLoading(true);

    setTimeout(() => {
      try {
        exportMeasurementsFile({
          telemetryInfo,
          telemetrySelection,
          selectedOption,
          shipmentCode,
          variant,
        });
        onClose();
        resetModal();
      } catch (error) {
        Modal.error({
          title: "Error al exportar mediciones",
          content: error.message || "Ocurrió un error al generar el archivo.",
        });
        setShowLoading(false);
        setStep("selection");
      }
    }, 200);
  };

  const handleStartDownload = async () => {
    if (["simpleReport", "detailedReport"].includes(selectedOption)) {
      const reportType = selectedOption === "simpleReport" ? 2 : 1;
      const fullTelemetry = variant === "full" ? true : false;
      await handleReportDownload(reportType, fullTelemetry);
    } else if (isStandalone) {
      exportTelemetryFile();
    } else {
      setStep("telemetryOptions");
    }
  };

  const handleConfirmTelemetryDownload = () => {
    exportTelemetryFile();
  };

  return (
    <Modal
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      centered
      maskClosable={false}
      closable={false}
      className="ReportModal"
    >
      {step === "selection" && (
        <SelectionStep
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          onCancel={handleCancel}
          onConfirm={handleStartDownload}
        />
      )}

      {step === "telemetryOptions" && (
        <TelemetryOptionsStep
          telemetrySelection={telemetrySelection}
          setTelemetrySelection={setTelemetrySelection}
          onCancel={() => setStep("selection")}
          onConfirm={handleConfirmTelemetryDownload}
        />
      )}

      {step === "downloading" && showLoading && (
        <DownloadingStep
          allowCancel={allowCancel}
          onCancel={handleCancel}
          type="report"
        />
      )}
    </Modal>
  );
}

export default withTranslation()(ReportModal);
